import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    DollarSign, Calendar, Clock, Lock, Sparkles, X,
    CheckCircle2, AlertCircle, Loader2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

const fmtCT = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            weekday: 'short', month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
        });
    } catch { return iso; }
};

/**
 * Countdown helper — formats the milliseconds remaining until `iso` as
 * "2d 14h 03m" / "3h 22m" / "12m 04s".
 */
const useCountdown = (iso) => {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);
    if (!iso) return '';
    const ms = Math.max(0, new Date(iso).getTime() - now);
    const d = Math.floor(ms / 86_400_000);
    const h = Math.floor((ms % 86_400_000) / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    const s = Math.floor((ms % 60_000) / 1000);
    if (d > 0) return `${d}d ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m ${String(s).padStart(2, '0')}s`;
    return `${m}m ${String(s).padStart(2, '0')}s`;
};

const PayDayCashoutDialog = ({ open, onClose, info, onSuccess }) => {
    const [amount, setAmount] = useState('');
    const [method, setMethod] = useState('');
    const [handle, setHandle] = useState('');
    const [email, setEmail] = useState('');
    const [nameOnAccount, setNameOnAccount] = useState('');
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Auto-prefill everything TapSNDR needs from the ambassador's last
    // cashout (whether it was a regular player or PAY DAY cashout) so
    // repeat requests only need Amount + Confirm.
    useEffect(() => {
        if (!open) return;
        setAmount(''); setNotes('');
        (async () => {
            try {
                const { data } = await axios.get(`${API}/cashout-settings`, { withCredentials: true });
                setMethod(data.last_payout_method || '');
                setHandle(data.last_payout_handle || '');
                setEmail(data.last_payout_email || '');
                setNameOnAccount(data.last_payout_name_on_account || '');
            } catch { /* silent, user fills manually */ }
        })();
    }, [open]);

    const max = info?.cashable_now || 0;
    const amt = parseFloat(amount);
    const methodNeedsHandle = method && method !== 'Cash';
    const handleOk = !methodNeedsHandle || (handle.trim().length >= 3);
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());
    const nameOk = (nameOnAccount || '').trim().split(/\s+/).length >= 2;
    const valid = amt > 0 && amt <= max + 0.001 && method && handleOk && emailOk && nameOk;

    // Match the player-side 6% fee so the ambassador sees exactly what
    // they'll receive after the deduction.
    const feeAmount = amt > 0 ? Math.round(amt * 0.06 * 100) / 100 : 0;
    const netAmount = amt > 0 ? Math.round((amt - feeAmount) * 100) / 100 : 0;

    const submit = async () => {
        if (!valid) return;
        setSubmitting(true);
        try {
            await axios.post(`${API}/ambassador/pay-day/request`, {
                amount: amt,
                payout_method: method,
                payout_handle: handle.trim(),
                payout_email: email.trim(),
                payout_name_on_account: nameOnAccount.trim(),
                notes: notes.trim() || undefined,
            }, { withCredentials: true });
            toast.success(`PAY DAY request for ${fmtMoney(amt)} submitted — you'll receive ${fmtMoney(netAmount)} after the 6% fee.`);
            onSuccess?.();
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not submit PAY DAY request');
        } finally {
            setSubmitting(false);
        }
    };

    if (!info) return null;
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="bg-[#121212] border-amber-500/30 max-w-md text-zinc-100" data-testid="pay-day-dialog">
                <DialogHeader>
                    <DialogTitle className="text-amber-400 flex items-center gap-2">
                        <Sparkles className="w-5 h-5" /> Cash Out — PAY DAY
                    </DialogTitle>
                    <DialogDescription className="text-zinc-400">
                        Earnings from <span className="text-amber-300 font-bold">{info.cashable_week_start}</span> – <span className="text-amber-300 font-bold">{info.cashable_week_end}</span>.
                        Available now: <span className="text-emerald-400 font-mono font-bold">{fmtMoney(max)}</span>.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="text-zinc-300">Amount</Label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-400 font-bold">$</span>
                            <Input
                                type="number" step="0.01" min="0.01" max={max.toFixed(2)}
                                value={amount} onChange={(e) => setAmount(e.target.value)}
                                placeholder="0.00"
                                className="bg-zinc-950 border-zinc-700 text-zinc-100 pl-7 font-mono text-lg"
                                data-testid="pay-day-amount"
                            />
                        </div>
                        <div className="flex gap-1.5 mt-1.5">
                            {[max * 0.25, max * 0.5, max].filter(v => v > 0).map((v, i) => (
                                <button key={i} type="button" onClick={() => setAmount(v.toFixed(2))}
                                    className="px-2 py-1 text-[10px] rounded border border-zinc-700 text-zinc-400 hover:border-amber-500/50 hover:text-amber-300"
                                    data-testid={`pay-day-quick-${i}`}
                                >
                                    {i === 2 ? 'MAX ' : ''}{fmtMoney(v)}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <Label className="text-zinc-300">Payout method</Label>
                        <Select value={method} onValueChange={setMethod}>
                            <SelectTrigger className="bg-zinc-950 border-zinc-700 text-zinc-100" data-testid="pay-day-method">
                                <SelectValue placeholder="How should we send it?" />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-950 border-zinc-700 text-zinc-100">
                                {(info.payment_methods || []).map(m => (
                                    <SelectItem key={m} value={m} data-testid={`pay-day-method-${m.replace(/\s+/g, '-').toLowerCase()}`}>{m}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    {methodNeedsHandle && (
                        <div>
                            <Label className="text-zinc-300">{method} handle</Label>
                            <Input
                                value={handle}
                                onChange={(e) => setHandle(e.target.value)}
                                placeholder={method === 'Zelle' ? 'Phone or email registered with your bank' : `Your ${method} handle`}
                                className="bg-zinc-950 border-zinc-700 text-zinc-100"
                                data-testid="pay-day-handle"
                            />
                        </div>
                    )}
                    <div>
                        <Label className="text-zinc-300">Name on account</Label>
                        <Input
                            value={nameOnAccount}
                            onChange={(e) => setNameOnAccount(e.target.value)}
                            placeholder="First and last (matches the wallet app)"
                            className="bg-zinc-950 border-zinc-700 text-zinc-100"
                            data-testid="pay-day-name-on-account"
                        />
                    </div>
                    <div>
                        <Label className="text-zinc-300">Email</Label>
                        <Input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Where TapSNDR sends the redemption link"
                            className="bg-zinc-950 border-zinc-700 text-zinc-100"
                            data-testid="pay-day-email"
                        />
                    </div>
                    {amt > 0 && (
                        <div
                            className="rounded-lg p-3 border border-amber-500/40 bg-amber-500/8 text-sm space-y-0.5"
                            data-testid="pay-day-fee-preview"
                        >
                            <div className="flex justify-between text-zinc-300">
                                <span>Gross request</span>
                                <span className="font-mono">{fmtMoney(amt)}</span>
                            </div>
                            <div className="flex justify-between text-red-300 text-xs">
                                <span>6% cashout fee</span>
                                <span className="font-mono">− {fmtMoney(feeAmount)}</span>
                            </div>
                            <div className="flex justify-between font-bold border-t border-white/10 pt-1 mt-1">
                                <span className="text-white">You&apos;ll receive</span>
                                <span className="font-mono text-emerald-400" data-testid="pay-day-net-amount">{fmtMoney(netAmount)}</span>
                            </div>
                            <p className="text-[10px] text-amber-300/80 pt-0.5">
                                Fee rebated Mondays for Elite ambassadors — same rules as players.
                            </p>
                        </div>
                    )}
                    <div>
                        <Label className="text-zinc-300">Notes (optional)</Label>
                        <Input
                            value={notes} onChange={(e) => setNotes(e.target.value)}
                            placeholder="e.g. Cash App handle, Zelle email"
                            className="bg-zinc-950 border-zinc-700 text-zinc-100"
                            maxLength={200}
                            data-testid="pay-day-notes"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button
                        onClick={submit}
                        disabled={!valid || submitting}
                        className="bg-gradient-to-r from-amber-500 to-amber-400 text-zinc-900 font-black hover:from-amber-400 hover:to-amber-300"
                        data-testid="pay-day-submit"
                    >
                        {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Submitting…</> : `Request ${amount ? fmtMoney(amt) : ''}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

/**
 * PAY DAY card — top of the affiliate dashboard.
 * - Window OPEN: pulsing amber, big cashout button, live countdown to close.
 * - Window CLOSED: lock icon, shows next-PAY-DAY countdown.
 */
const AmbassadorPayDayCard = ({ onSubmitted }) => {
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [pendingRequest, setPendingRequest] = useState(null);

    const fetchInfo = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/ambassador/pay-day`, { withCredentials: true });
            setInfo(data);
        } catch {
            // Silently no-op — non-ambassador users may hit this. Card hides.
            setInfo({ window_open: false, lifetime_pending: 0, lifetime_paid: 0, cashable_now: 0 });
        }
        try {
            // Surface a "your request is in review" strip if a pending one exists.
            const { data: hist } = await axios.get(`${API}/ambassador/pay-day/requests`, { withCredentials: true });
            const open = (hist?.requests || []).find(r => r.status === 'pending');
            setPendingRequest(open || null);
        } catch { /* endpoint optional */ }
        setLoading(false);
    }, []);

    useEffect(() => { fetchInfo(); }, [fetchInfo]);

    const countdownToClose = useCountdown(info?.window_closes_at);
    const countdownToOpen  = useCountdown(info?.window_opens_at);

    if (loading) {
        return (
            <div className="rounded-lg border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent p-5 flex items-center justify-center min-h-[120px]">
                <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />
            </div>
        );
    }
    if (!info) return null;

    const isOpen = info.window_open;
    const hasFunds = (info.cashable_now || 0) > 0;

    return (
        <>
            <div
                className={`relative rounded-xl border-2 p-5 overflow-hidden transition-all ${
                    isOpen
                        ? 'border-amber-400/60 bg-gradient-to-br from-amber-500/20 via-amber-500/8 to-transparent shadow-[0_0_40px_-12px_rgba(251,191,36,0.5)]'
                        : 'border-zinc-800 bg-[#121212]'
                }`}
                data-testid="pay-day-card"
            >
                {isOpen && (
                    <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full bg-amber-500 text-zinc-900">
                        <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-zinc-900 opacity-60" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-zinc-900" />
                        </span>
                        LIVE
                    </span>
                )}

                <div className="flex items-start gap-3">
                    {isOpen ? (
                        <Sparkles className="w-7 h-7 text-amber-400 flex-shrink-0 mt-1" />
                    ) : (
                        <Lock className="w-6 h-6 text-zinc-500 flex-shrink-0 mt-1" />
                    )}
                    <div className="flex-1 min-w-0">
                        <p className={`text-[10px] uppercase tracking-[0.2em] font-black ${isOpen ? 'text-amber-300' : 'text-zinc-500'}`}>
                            PAY DAY
                        </p>
                        <h2 className={`text-3xl sm:text-4xl font-black mt-1 leading-none ${isOpen ? 'text-amber-300' : 'text-zinc-400'}`}>
                            {isOpen ? 'Cashout is OPEN' : 'See you Monday'}
                        </h2>
                        <p className="text-xs text-zinc-400 mt-1.5 flex items-center gap-1.5 flex-wrap">
                            <Calendar className="w-3.5 h-3.5" />
                            Earnings for <span className="text-amber-300 font-bold">{info.cashable_week_start}</span> – <span className="text-amber-300 font-bold">{info.cashable_week_end}</span>
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mt-5">
                    <div className="rounded-lg bg-zinc-950/60 border border-zinc-800 px-3 py-2">
                        <p className="text-[9px] text-zinc-500 uppercase tracking-widest">Cashable Now</p>
                        <p className={`text-2xl font-mono font-black ${hasFunds ? 'text-emerald-400' : 'text-zinc-600'}`}
                           data-testid="pay-day-cashable-amount">
                            {fmtMoney(info.cashable_now)}
                        </p>
                    </div>
                    <div className="rounded-lg bg-zinc-950/60 border border-zinc-800 px-3 py-2">
                        <p className="text-[9px] text-zinc-500 uppercase tracking-widest">In Review</p>
                        <p className="text-2xl font-mono font-black text-amber-300">{fmtMoney(info.requested_pending)}</p>
                    </div>
                    <div className="rounded-lg bg-zinc-950/60 border border-zinc-800 px-3 py-2">
                        <p className="text-[9px] text-zinc-500 uppercase tracking-widest">Paid Lifetime</p>
                        <p className="text-2xl font-mono font-black text-zinc-300">{fmtMoney(info.lifetime_paid)}</p>
                    </div>
                </div>

                <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 text-xs text-zinc-400">
                        <Clock className="w-3.5 h-3.5" />
                        {isOpen ? (
                            <span>
                                Closes in <span className="text-amber-300 font-mono font-bold" data-testid="pay-day-countdown-close">{countdownToClose}</span>
                                <span className="text-zinc-600"> · {fmtCT(info.window_closes_at)}</span>
                            </span>
                        ) : (
                            <span>
                                Opens in <span className="text-zinc-300 font-mono font-bold" data-testid="pay-day-countdown-open">{countdownToOpen}</span>
                                <span className="text-zinc-600"> · {fmtCT(info.window_opens_at)}</span>
                            </span>
                        )}
                    </div>
                    {isOpen ? (
                        <Button
                            disabled={!hasFunds}
                            onClick={() => setDialogOpen(true)}
                            className="bg-gradient-to-r from-amber-500 to-amber-400 text-zinc-900 font-black hover:from-amber-400 hover:to-amber-300 disabled:opacity-40"
                            data-testid="pay-day-cashout-btn"
                        >
                            <DollarSign className="w-4 h-4 mr-1" />
                            {hasFunds ? `Cash Out ${fmtMoney(info.cashable_now)}` : 'Nothing to cash out'}
                        </Button>
                    ) : (
                        <Button
                            disabled
                            className="bg-zinc-800 text-zinc-500 cursor-not-allowed"
                            data-testid="pay-day-cashout-btn"
                            title="Opens Monday at 12:00 AM Central"
                        >
                            <Lock className="w-4 h-4 mr-1" /> Opens Monday 12 AM CT
                        </Button>
                    )}
                </div>

                {pendingRequest && (
                    <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs flex items-start gap-2" data-testid="pay-day-pending-strip">
                        <AlertCircle className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <p className="text-amber-200">
                                <span className="font-black">Request in review:</span> {fmtMoney(pendingRequest.amount)} via {pendingRequest.payout_method}.
                                Filed {fmtCT(pendingRequest.created_at)}.
                            </p>
                            <p className="text-[10px] text-zinc-500">Admin will mark it paid after sending the funds.</p>
                        </div>
                    </div>
                )}
            </div>

            <PayDayCashoutDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                info={info}
                onSuccess={() => { fetchInfo(); onSubmitted?.(); }}
            />
        </>
    );
};

export default AmbassadorPayDayCard;
